import { eventDefinitions } from './events';
import { SNOWPLOW_SCHEMAS } from './schemas';

import type { SnowplowSchemaName } from './schemas';
import type {
  CreateContextsParams,
  ContextParams,
  SnowplowUnstructuredEvent,
  SnowplowEventPayload,
} from './types';

/**
 * Creates Snowplow context objects for the given entity keys
 * @param entityKeys - Array of entity keys to create contexts for
 * @param params - Optional parameters for context creation
 * @param params.eventData - The event data containing properties matching the entity keys
 *                           or data to be transformed. Will default to empty object if not provided,
 *                           but typically you'll want to provide data for your contexts.
 * @param params.transformers - Object mapping entity keys to transformer functions that
 *                              modify the data before creating contexts. Use when the data
 *                              needs to be transformed from its raw form.
 * @param params.filterData - Required if your transformers need access to filter data
 *
 * @example
 * // Basic usage with direct property mapping
 * createContexts(['user_context'], {
 *   eventData: { user_context: { id: 123, name: 'John' } }
 * })
 *
 * @example
 * // Using transformers to shape the data
 * createContexts(['filtered_context'], {
 *   eventData: { rawData: { count: 5 } },
 *   filterData: { type: 'active' },
 *   transformers: {
 *     filtered_context: (eventData, filterData) => ({
 *       count: eventData.rawData.count,
 *       type: filterData.type
 *     })
 *   }
 * })
 */
export const createContexts = (
  entityKeys: SnowplowSchemaName[],
  params: CreateContextsParams = {},
) => {
  const { eventData = {}, transformers, filterData } = params;

  return entityKeys.map((entityKey) => ({
    schema: SNOWPLOW_SCHEMAS[entityKey],
    data: transformers?.[entityKey]
      ? transformers[entityKey](eventData, filterData)
      : eventData[entityKey] ?? {},
  }));
};

export const createSnowplowEvent = (
  event: SnowplowUnstructuredEvent,
  params: Omit<ContextParams, 'action'> = {},
): SnowplowEventPayload => {
  const { action, category } = event;

  const definition = eventDefinitions[category];
  if (!definition) {
    throw new Error(`Unknown event category: ${category}`);
  }

  const snowplowEvent: SnowplowEventPayload = {
    event: {
      schema: SNOWPLOW_SCHEMAS[category],
      data: definition.getData(action, params.eventData),
    },
    context: definition.addContexts?.({ action, ...params }) ?? [],
  };

  return snowplowEvent;
};
